import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Redirect } from "react-router-dom";

import Button from "../../../../components/Button";
import Input, { MaterialInputEvent } from "../../../../components/v2/Input";
import Format from "../../../../helpers/Format";
import AdminTransparencyService, {
  TransparencyAttorney,
  TransparencyPeriodDetail,
  TransparencyPeriodSummary,
  isPublishConflict,
} from "../../../../services/AdminTransparencyService";
import PublicationDialog, { PublicationAction } from "./PublicationDialog";
import StatusTag from "./StatusTag";
import { usePeriodTitle } from "./usePeriodTitle";

import style from "./styles.module.scss";

const keyI18n = "translations.admin.transparency";

type Status = "loading" | "ready" | "notFound" | "error";
type Tab = "attorneys" | "legalBasis";

const TABS: Tab[] = ["attorneys", "legalBasis"];

interface Props {
  id: number;
  backLink?: string;
  publishedSibling?: TransparencyPeriodSummary | null;
  onChanged?(): void;
}

export default function PeriodDetail({ id, backLink, publishedSibling, onChanged }: Props) {
  const { t } = useTranslation();
  const periodTitle = usePeriodTitle();
  const tabRefs = useRef<{ [key in Tab]?: HTMLButtonElement | null }>({});

  const [period, setPeriod] = useState<TransparencyPeriodDetail | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [tab, setTab] = useState<Tab>("attorneys");
  const [action, setAction] = useState<PublicationAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(false);

  useEffect(() => {
    AdminTransparencyService.period(id)
      .then((data) => {
        setPeriod(data);
        setStatus("ready");
      })
      .catch((error) => setStatus(isNotFound(error) ? "notFound" : "error"));
  }, [id]);

  if (status === "notFound") {
    return <Redirect to="/404" />;
  }

  const backButton = backLink && (
    <Link to={backLink} className={style.backLink} aria-label={t(`${keyI18n}.actions.back`)}>
      <span aria-hidden="true" className="material-symbols-sharp">arrow_back</span>
    </Link>
  );

  if (status !== "ready" || !period) {
    return (
      <div className={style.detail}>
        {backButton}
        <p className={`pgm-body-large ${style.message}`} role={status === "error" ? "alert" : "status"}>
          {status === "error" ? t(`${keyI18n}.detailError`) : t("translations.admin.home.loading")}
        </p>
      </div>
    );
  }

  const published = period.status === "published";

  const openPublication = () => {
    setActionError(false);
    setAction(published ? "unpublish" : publishedSibling ? "replace" : "publish");
  };

  const confirmPublication = () => {
    const request = action === "unpublish"
      ? AdminTransparencyService.unpublish(period.id)
      : AdminTransparencyService.publish(period.id, action === "replace");

    setBusy(true);
    request
      .then((summary) => {
        setPeriod({ ...period, status: summary.status });
        setAction(null);
        if (onChanged) {
          onChanged();
        }
      })
      .catch((error) => {
        if (isPublishConflict(error)) {
          setAction("replace");
        } else {
          setAction(null);
          setActionError(true);
        }
      })
      .finally(() => setBusy(false));
  };

  const saveNotes = (feesNote: string, aidsNote: string) =>
    AdminTransparencyService.updateNotes(period.id, feesNote, aidsNote).then((data) => {
      setPeriod({ ...period, feesNote: data.feesNote, aidsNote: data.aidsNote });
    });

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const index = TABS.indexOf(tab);
    const offsets: { [key: string]: number } = { ArrowRight: 1, ArrowLeft: -1 };
    let next: Tab | undefined;

    if (event.key in offsets) {
      next = TABS[(index + offsets[event.key] + TABS.length) % TABS.length];
    } else if (event.key === "Home") {
      next = TABS[0];
    } else if (event.key === "End") {
      next = TABS[TABS.length - 1];
    }

    if (next) {
      event.preventDefault();
      setTab(next);
      const element = tabRefs.current[next];
      if (element) {
        element.focus();
      }
    }
  };

  const tabLabels: { [key in Tab]: string } = {
    attorneys: t(`${keyI18n}.tabs.attorneys`, { count: period.attorneys.length }),
    legalBasis: t(`${keyI18n}.tabs.legalBasis`),
  };

  return (
    <div className={style.detail}>
      <header className={style.detailHeader}>
        {backButton}
        <div className={style.detailHeading}>
          <h2 className={`pgm-headline-small ${style.detailTitle}`}>{periodTitle(period.month, period.year)}</h2>
          <StatusTag status={period.status} />
        </div>
        <Button
          variant={published ? "outlined" : "filled"}
          shape="round"
          size="small"
          onClick={openPublication}
          icon={<span className="material-symbols-sharp">{published ? "unpublished" : "publish"}</span>}
        >
          {t(`${keyI18n}.actions.${published ? "unpublish" : "publish"}`)}
        </Button>
      </header>

      {actionError && (
        <p className={`pgm-body-medium ${style.actionError}`} role="alert">{t(`${keyI18n}.publication.error`)}</p>
      )}

      <div className={style.tabs} role="tablist" aria-label={t(`${keyI18n}.tabs.label`)}>
        {TABS.map((key) => (
          <button
            key={key}
            ref={(element) => { tabRefs.current[key] = element; }}
            type="button"
            role="tab"
            id={`transparency-tab-${key}`}
            aria-controls={`transparency-panel-${key}`}
            aria-selected={tab === key}
            tabIndex={tab === key ? 0 : -1}
            className={`pgm-title-small ${style.tab} ${tab === key ? style.tabActive : ""}`}
            onClick={() => setTab(key)}
            onKeyDown={handleTabKeyDown}
          >
            <span className={style.tabLabel}>{tabLabels[key]}</span>
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`transparency-panel-${tab}`}
        aria-labelledby={`transparency-tab-${tab}`}
        className={style.tabPanel}
      >
        {tab === "attorneys" ? (
          <AttorneyList attorneys={period.attorneys} />
        ) : published ? (
          <div className={style.notes}>
            <Note label={t(`${keyI18n}.notes.fees`)} value={period.feesNote} />
            <Note label={t(`${keyI18n}.notes.aids`)} value={period.aidsNote} />
          </div>
        ) : (
          <NotesForm
            key={`${period.feesNote}|${period.aidsNote}`}
            feesNote={period.feesNote}
            aidsNote={period.aidsNote}
            onSave={saveNotes}
          />
        )}
      </div>

      <PublicationDialog
        action={action}
        replacedTitle={periodTitle(period.month, period.year)}
        busy={busy}
        onCancel={() => setAction(null)}
        onConfirm={confirmPublication}
      />
    </div>
  );
}

interface NotesFormProps {
  feesNote: string | null;
  aidsNote: string | null;
  onSave(feesNote: string, aidsNote: string): Promise<void>;
}

function NotesForm({ feesNote, aidsNote, onSave }: NotesFormProps) {
  const { t } = useTranslation();
  const [fees, setFees] = useState(feesNote || "");
  const [aids, setAids] = useState(aidsNote || "");
  const [state, setState] = useState<"idle" | "saving" | "error">("idle");
  const changed = fees !== (feesNote || "") || aids !== (aidsNote || "");

  const save = () => {
    setState("saving");
    onSave(fees, aids)
      .then(() => setState("idle"))
      .catch(() => setState("error"));
  };

  return (
    <div className={style.notes}>
      <Input
        type="textarea"
        variant="outlined"
        rows={5}
        fullWidth
        label={t(`${keyI18n}.notes.fees`)}
        placeholder={t(`${keyI18n}.notes.placeholder`)}
        value={fees}
        onInput={(event: MaterialInputEvent) => setFees(event.target.value)}
      />
      <Input
        type="textarea"
        variant="outlined"
        rows={5}
        fullWidth
        label={t(`${keyI18n}.notes.aids`)}
        placeholder={t(`${keyI18n}.notes.placeholder`)}
        value={aids}
        onInput={(event: MaterialInputEvent) => setAids(event.target.value)}
      />
      {state === "error" && (
        <p className={`pgm-body-medium ${style.actionError}`} role="alert">{t(`${keyI18n}.notes.saveError`)}</p>
      )}
      <div className={style.notesActions}>
        <Button variant="filled" shape="round" size="small" onClick={save} disabled={!changed || state === "saving"}>
          {t(`${keyI18n}.notes.save`)}
        </Button>
      </div>
    </div>
  );
}

function AttorneyList({ attorneys }: { attorneys: TransparencyAttorney[] }) {
  const { t } = useTranslation();

  if (attorneys.length === 0) {
    return <p className={`pgm-body-large ${style.message}`}>{t(`${keyI18n}.noAttorneys`)}</p>;
  }

  return (
    <ul className={style.attorneys}>
      {attorneys.map((attorney) => (
        <li key={attorney.id} className={style.attorney}>
          <h3 className={`pgm-title-medium ${style.attorneyName}`}>{attorney.name}</h3>
          <ValueRow
            title={t(`${keyI18n}.fees.title`)}
            values={[
              [t(`${keyI18n}.fees.gross`), attorney.fees.gross],
              [t(`${keyI18n}.fees.otherDeductions`), attorney.fees.otherDeductions],
              [t(`${keyI18n}.fees.incomeTax`), attorney.fees.incomeTax],
              [t(`${keyI18n}.fees.net`), attorney.fees.net],
            ]}
          />
          <ValueRow
            title={t(`${keyI18n}.aids.title`)}
            values={[
              [t(`${keyI18n}.aids.health`), attorney.aids.health],
              [t(`${keyI18n}.aids.meal`), attorney.aids.meal],
            ]}
          />
        </li>
      ))}
    </ul>
  );
}

function ValueRow({ title, values }: { title: string; values: Array<[string, number | null]> }) {
  return (
    <div className={style.valueRow}>
      <span className={`pgm-title-small ${style.valueRowTitle}`}>{title}</span>
      <dl className={style.values}>
        {values.map(([label, value]) => (
          <div key={label} className={style.value}>
            <dt className={`pgm-label-medium ${style.valueLabel}`}>{label}</dt>
            <dd className={`pgm-body-large ${style.valueAmount}`}>{Format.currency(value ?? 0)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Note({ label, value }: { label: string; value: string | null }) {
  const { t } = useTranslation();
  const text = value && value.trim();

  return (
    <section className={style.note}>
      <h3 className={`pgm-title-small ${style.noteLabel}`}>{label}</h3>
      <p className={`pgm-body-large ${style.noteText} ${text ? "" : style.noteEmpty}`}>
        {text || t(`${keyI18n}.notes.empty`)}
      </p>
    </section>
  );
}

function isNotFound(error: unknown): boolean {
  return typeof error === "object" && error !== null && (error as { error?: string }).error === "not_found";
}
